import { t } from "@lingui/core/macro";
import type { Optional } from "@regrello/core-utils";
import type {
  ActionItemFields,
  DocumentFields,
  FormVersionFields,
  Maybe,
  PartyFields,
  RegrelloObject,
  RoleFields,
  TeamFields,
  UserFieldsWithRoles,
  WorkflowFields,
  WorkflowPermissionsFields,
  WorkflowQueryActionItemTemplateCardFields,
  WorkflowQueryActionItemTemplateFields,
  WorkflowTemplateFields,
} from "@regrello/graphql-api";
import type { CurrentUserContext } from "../../../types";
import type { UserContextType } from "../init/UserContext";

/**
 * `ActionItemFieldsPartial<"fieldA" | "fieldB">` names any type adhering to `ActionItemFields`
 * as an interface and supplying required fields `fieldA` and `fieldB`.
 */
export type TaskFieldsPartial<T extends keyof ActionItemFields> = Pick<ActionItemFields, T>;

/**
 * A convenient client-side permission-checking API.
 *
 * Checks in this API will typically consult boolean flags provided from the backend (e.g.,
 * `actionItem.permissions.canRead`). However, they may occasionally weave in custom client-side
 * checks if a particular scenario is not covered by the backend; in such cases, we should aim to
 * shift this custom logic to the backend eventually, to ensure we're using consistent permissions
 * logic throughout the platform.
 *
 * @example
 * if (RegrelloPermissions.ActionItem.canView(actionItem)) {
 *   // Render the action item.
 * }
 */
export namespace RegrelloPermissions {
  export namespace User {
    export function isInternal(
      user: Pick<UserFieldsWithRoles, "accessLevel"> | Pick<CurrentUserContext, "accessLevel">,
    ): boolean {
      return user.accessLevel === "INTERNAL";
    }
    export function isLimited(
      user: Pick<UserFieldsWithRoles, "accessLevel"> | Pick<CurrentUserContext, "accessLevel">,
    ): boolean {
      return user.accessLevel === "LIMITED";
    }
  }

  export namespace ActionItem {
    export function canView(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canRead;
    }

    export function canDelete(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canDelete;
    }

    export function canDuplicate(
      actionItem: Pick<ActionItemFields, "permissions" | "type" | "approvalForActionItem">,
      workflow: Pick<WorkflowFields, "permissions">,
    ) {
      const isApprovalTask = actionItem.approvalForActionItem != null;
      const canAddLinkedWorkflowTask = workflow.permissions.canAddLinkedWorkflowTask;

      // (akager) You can duplicate an action item if you have permission to create tasks in the
      // parent workflow (workflow.canEdit) and if you have view access on the action item
      // (actionItem.canRead). (zstanik): Additionally, automated tasks can't be duplicated and were
      // just being duplicated as standard tasks.
      return (
        RegrelloPermissions.Workflow.canEdit(workflow) &&
        RegrelloPermissions.ActionItem.canView(actionItem) &&
        !isApprovalTask &&
        actionItem.type !== "AUTOMATION" &&
        (actionItem.type !== "LINKED_WORKFLOW" || canAddLinkedWorkflowTask)
      );
    }

    export function canEdit(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEdit;
    }

    export function canReopen(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canReopen;
    }

    export function canEditName(actionItem: ActionItemFields) {
      return actionItem.permissions?.canEditName;
    }

    export function canEditAssignees(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditAssignees;
    }

    export function canEditDate(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditDueOn;
    }

    export function canEditCcs(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditCC;
    }

    export function canEditDescription(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditDescription;
    }

    export function canEditEmailSubject(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditEmailSubject;
    }

    export function canComment(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canComment;
    }

    export function canEditTags(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canEditTags;
    }

    export function canSubmitOrReopen(actionItem: TaskFieldsPartial<"permissions" | "type">) {
      // TODO Linked Workflows: remove check for type once the BE checks this in the permissions
      // resolver.
      return actionItem.permissions?.canComplete && actionItem.type !== "LINKED_WORKFLOW";
    }

    export function canReportOrResolveException(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canReportException;
    }

    export function canApproveOrReject(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canApprove;
    }

    export function canStar(actionItem: TaskFieldsPartial<"permissions">) {
      return actionItem.permissions?.canRead;
    }
  }

  export namespace ActionItemTemplate {
    /** @deprecated Use `ActionItemTemplateV2.canView`, which is backed by our permissions model, instead. */
    export function canView(
      actionItemTemplate: Optional<
        Pick<
          WorkflowQueryActionItemTemplateCardFields,
          "actionItems" | "approvalActionItemTemplates" | "assignedBy" | "assignees" | "cc"
        >,
        "actionItems" | "approvalActionItemTemplates"
      >,
      workflowCollaborators: PartyFields[],
      currentUser: UserContextType["currentUser"],
    ) {
      const isAssociatedWithThisTaskTemplate = [
        [actionItemTemplate.assignedBy],
        actionItemTemplate.assignees,
        actionItemTemplate.cc,
      ].some((parties) => parties.some((party) => party.id === currentUser.partyId));

      const isWorkflowCollaborator = workflowCollaborators.some((party) => party.id === currentUser.partyId);

      return currentUser.isAdmin || isAssociatedWithThisTaskTemplate || isWorkflowCollaborator;
    }
  }

  export namespace ActionItemTemplateV2 {
    export function canDelete(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canDelete;
    }

    export function canDuplicate(
      actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions" | "type">,
    ) {
      // (akager) This permission check is not quite correct. The permission to duplicate a task
      // requires the ability to view the task, and the ability to add a task to the workflow
      // the task is in. It is not related to the ability to edit that task itself. Not
      // changing this for now as it would require a lot of changes and this logic should be correct
      // for the foreseable future.
      return (
        actionItemTemplate.permissions.canEdit &&
        (actionItemTemplate.type === "DEFAULT" ||
          actionItemTemplate.type === "APPROVAL" ||
          actionItemTemplate.type === "LINKED_WORKFLOW")
      );
    }

    export function canEdit(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canEdit;
    }

    export function canStart(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canStart;
    }

    export function canView(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canRead;
    }

    export function canEditApprovals(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canEditApprovals;
    }

    export function canEditApprovers(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canEditApprovers;
    }

    export function canEditAssignees(actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">) {
      return actionItemTemplate.permissions.canEditAssignees;
    }

    export function canRemoveRequestedFields(
      actionItemTemplate: Pick<WorkflowQueryActionItemTemplateFields, "permissions">,
    ) {
      return actionItemTemplate.permissions.canRemoveRequestedFields;
    }
  }

  export namespace Create {
    export function canCreateAutomations(currentUser: UserContextType["currentUser"]) {
      return currentUser.isAdmin;
    }

    export function canCreateBlueprints(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreateTemplateButton;
    }

    export function canCreateCustomFields(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.customFieldPermissions.canCreate;
    }

    export function canCreateRoles(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreateRoleButton;
    }

    /**
     * Whether the current user can control the setting on a role which grants members of that role
     * the `user.create` permission. Creators have the normal `role.create` permission, but not this
     * one, hence the distinction.
     */
    export function canCreateRolesWithInvite(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
      return currentUser.permissions.canCreateRolesWithInvite;
    }

    export function canCreatePeople(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreatePeopleButton;
    }

    export function canCreateTags(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.canCreateTags;
    }

    export function canCreateUsers(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreatePeopleButton;
    }

    export function canCreateWorkflows(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreateWorkflowButton;
    }

    export function canCreateTeams(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
      return currentUser.permissions.permissionedNavigationFlags.createMenuContentFlags.showCreateTeamButton;
    }

    export function canCreateForms(currentUser: UserContextType["currentUser"]) {
      return currentUser.accessLevel === "INTERNAL";
    }
  }

  export namespace CustomField {
    export function canDelete(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.customFieldPermissions.canDelete;
    }
    export function canEdit(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.customFieldPermissions.canEdit;
    }
  }

  export namespace DataSource {
    export function canEdit(dataSource: Pick<RegrelloObject, "permissions">) {
      return dataSource.permissions.canEdit ?? false;
    }
  }

  export namespace Document {
    export function canDeleteDocument(
      document: Pick<DocumentFields, "actionItem" | "currentVersion" | "permissions">,
      currentUser: Pick<UserContextType["currentUser"], "isAdmin" | "id">,
    ) {
      const isTaskCompleted = document.actionItem?.status === "COMPLETED";
      const isUploadedByCurrentUser = document.currentVersion.createdBy.id === currentUser.id;
      const canCurrentUserDelete = currentUser.isAdmin || (isUploadedByCurrentUser && !isTaskCompleted);
      const reasonWhyNotDeletable = canCurrentUserDelete
        ? undefined
        : isUploadedByCurrentUser
          ? isTaskCompleted
            ? t`Documents cannot be deleted from a completed task`
            : undefined
          : t`You cannot delete documents uploaded by other people`;

      return { canCurrentUserDelete: document.permissions.canDelete, reasonWhyNotDeletable };
    }
  }

  export namespace Navigation {
    export namespace Sidebar {
      export function canViewAdminPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPage;
      }
      export function canViewHomePage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.homePage;
      }
      export function canViewPeopleAndTeamsPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.peopleAndTeamsPage;
      }
      export function canViewTasksPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.assignedTasksPage;
      }
      export function canViewDocumentsPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.documentsPage;
      }
      export function canViewStarredPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.starredPage;
      }
      export function canViewBlueprintsPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.templatesSection.blueprints;
      }
      export function canViewFieldsPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.templatesSection.fieldsPage;
      }
      export function canViewTagsPage(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.templatesSection.tagsPage;
      }
    }
    export namespace AdminPage {
      export function canViewAiAgentsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.aiAgents;
      }
      export function canViewDataSourcesTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.dataSources;
      }
      export function canViewIntegrationsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.integrations;
      }
      export function canViewPeopleTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.people;
      }
      export function canViewRolesTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.roles;
      }
      export function canViewSettingsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.settings;
      }
      export function canViewTeamsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.adminPageTabs.teams;
      }
    }

    export namespace BlueprintsPage {
      export function canViewDraftBlueprintsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.templatesSection
          .draftBlueprintsPage;
      }
      export function canViewSharedBlueprintsTab(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
        return currentUser.permissions.permissionedNavigationFlags.sidebarContentFlags.templatesSection
          .sharedBlueprintsPage;
      }
    }
  }

  export namespace Role {
    export function canAssign(role: Pick<RoleFields, "permissions">) {
      return role.permissions?.canAssign ?? false;
    }
  }

  export namespace Tag {
    export function canDelete(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.tagPermissions.canDelete;
    }
    export function canEdit(currentUser: UserContextType["currentUser"]) {
      return currentUser.permissions.tagPermissions.canEdit;
    }
  }

  export namespace Team {
    // Reading teams happens on the per-team level. You may be able to read some teams but not others.
    export function canRead(team: TeamFields) {
      return team.permissions?.canRead ?? false;
    }

    // Editing teams happens at the workspace level. You can either edit all teams or no teams.
    export function canEditTeams(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
      return currentUser.permissions.teamPermissions.canEdit;
    }

    // Deleting teams happens at the workspace level. You can either delete all teams or no teams.
    export function canDeleteTeams(currentUser: Pick<UserContextType["currentUser"], "permissions">) {
      return currentUser.permissions.teamPermissions.canDelete;
    }
  }

  export namespace Tenant {
    export function canUpdateTenantDisplayName(currentUser: UserContextType["currentUser"]) {
      return currentUser.isAdmin;
    }
  }

  export namespace Workflow {
    export function canView(workflow: { id: number; permissions: Partial<WorkflowPermissionsFields> | undefined }) {
      return workflow.permissions?.canRead ?? false;
    }

    export function canEditStages(workflow: WorkflowFields) {
      return workflow.permissions?.canEdit ?? false;
    }

    export function canEditCollaborators(workflow: WorkflowFields) {
      return workflow.permissions?.canEdit ?? false;
    }

    export function canEdit(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow.permissions?.canEdit ?? false;
    }

    export function canEditData(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow.permissions?.canEditData ?? false;
    }

    export function canEditOwner(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow.permissions?.permissionsToWorkflowOwner.canEdit;
    }

    export function canStart(workflow: WorkflowFields) {
      return workflow.permissions?.canStart ?? false;
    }

    export function canDelete(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow.permissions?.canDelete ?? false;
    }

    export function canAddAutomation(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow?.permissions?.canAddAutomatedTask ?? false;
    }

    export function canAddLinkedWorkflowTask(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow?.permissions?.canAddLinkedWorkflowTask ?? false;
    }

    export function canStar(workflow: Pick<WorkflowFields, "permissions">) {
      return workflow.permissions?.canRead;
    }
  }

  export namespace WorkflowTemplate {
    export function canAddAutomation(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate?.permissions?.canAddAutomatedTask ?? false;
    }

    export function canAddLinkedWorkflowTask(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate?.permissions?.canAddLinkedWorkflowTask ?? false;
    }

    export function canEditCollaborators(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canEdit ?? false;
    }

    export function canEditWorkflowOwner(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.CanEditWorkflowOwner ?? false;
    }

    export function canDelete(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canDelete ?? false;
    }

    export function canDownload(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canDownload ?? false;
    }

    export function canCreateWorkflowsFrom(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canCreateWorkflowsFrom ?? false;
    }

    export function canEdit(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canEdit ?? false;
    }

    export function canPublish(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canPublish ?? false;
    }

    export function canCopy(workflowTemplate: Pick<WorkflowTemplateFields, "permissions">) {
      return workflowTemplate.permissions?.canCopy ?? false;
    }

    export function canView(workflowTemplate: { permissions?: Maybe<{ canRead?: Maybe<boolean> }> }) {
      return workflowTemplate.permissions?.canRead ?? false;
    }

    export function canExport(workflowTemplate: { permissions?: Maybe<{ canExport?: Maybe<boolean> }> }) {
      return workflowTemplate.permissions?.canExport ?? false;
    }

    export function canUpdateToNewVersion(workflowTemplate: {
      permissions?: Maybe<{ canUpdateToNewVersion?: Maybe<boolean> }>;
    }) {
      return workflowTemplate.permissions?.canUpdateToNewVersion ?? false;
    }

    export function canCreateVariantsOf(workflowTemplate: {
      permissions?: Maybe<{ canCreateVariantsOf?: Maybe<boolean> }>;
    }) {
      return workflowTemplate.permissions?.canCreateVariantsOf ?? false;
    }
  }

  // (hchen): HACKHACK This is a temporary permission check for the new form feature. We will need to
  // integrate with Permissions V2 when it's ready
  export namespace Form {
    export function canPublish(
      currentUser: UserContextType["currentUser"],
      userRoles: Array<{
        role: RoleFields;
      }>,
    ) {
      // (wsheehan): TODOWS: This is a temporary workaround which should not be used once forms live
      // within a blueprint.
      return currentUser.isAdmin || userRoles.some((role) => role.role.name === "Publisher" && role.role.isSystem);
    }

    export function canEdit(form?: FormVersionFields["form"]) {
      if (form == null) {
        return false;
      }
      return (
        form.workflow?.permissions.canEdit ||
        (form.workflowTemplate?.permissions?.canEdit && form.workflowTemplate?.publishedAt == null)
      );
    }
  }
}
